import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export default function BillingReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, payment: null });
  const [referralCode, setReferralCode] = useState(null);

  // After successful payment, fetch the user's referrals (founders get a new code minted)
  useEffect(() => {
    if (state.status !== "paid") return;
    api.get("/billing/referrals/my").then((r) => {
      const fresh = (r.data || []).find((c) => (c.uses || 0) === 0);
      if (fresh) setReferralCode(fresh);
    }).catch(() => {});
  }, [state.status]);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session id" });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setState({ status: "paid", attempts, payment: r.data });
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "expired", attempts, payment: r.data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", attempts, payment: r.data });
          return;
        }
        setState({ status: "polling", attempts, payment: r.data });
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setState({ status: "error", error: e?.response?.data?.detail || "Could not check status" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0a0a0a]" data-testid="billing-return-root">
      <div className="max-w-md w-full surface rounded-md p-10 text-center grain-overlay">
        {state.status === "polling" && (
          <>
            <Loader2 size={28} className="text-[#c89f45] mx-auto mb-6 animate-spin" />
            <p className="font-serif text-2xl">Finalizing your seat on the board…</p>
            <p className="text-xs text-[#666] mt-3 font-mono">Attempt {state.attempts}/{MAX_ATTEMPTS}</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <Crown size={28} className="text-[#c89f45] mx-auto mb-6" />
            <p className="label-eyebrow">Welcome to the board</p>
            <p className="font-serif text-3xl mt-3">You're upgraded.</p>
            <p className="text-sm text-[#888] mt-3">
              {state.payment?.metadata?.tier === "syndicate" ? "Syndicate" : "Boardroom Pro"} is now active.
            </p>

            {referralCode && (
              <div className="mt-8 p-5 rounded-md border border-[#c89f45]/40 bg-[#c89f45]/5 text-left" data-testid="founder-referral-block">
                <p className="label-eyebrow text-[#c89f45]">Your founding referral code</p>
                <p className="font-mono text-3xl mt-3 tracking-tighter text-[#fafafa] text-center select-all" data-testid="founder-code">{referralCode.code}</p>
                <p className="text-xs text-[#888] mt-3 text-center">
                  Share with one person — they get 30% off Pro or Syndicate. Single-use.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button
                    onClick={() => { navigator.clipboard.writeText(referralCode.code); toast.success("Code copied"); }}
                    variant="outline"
                    className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white"
                    data-testid="founder-copy-code"
                  >
                    <Copy size={14} className="mr-2" /> Code
                  </Button>
                  <Button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pricing?ref=${referralCode.code}`); toast.success("Share link copied"); }}
                    className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]"
                    data-testid="founder-copy-link"
                  >
                    <Share2 size={14} className="mr-2" /> Share link
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-2">
              <Link to="/" data-testid="return-to-dashboard">
                <Button className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md w-full">Enter the Boardroom</Button>
              </Link>
              <Link to="/pricing" className="text-xs text-[#666] hover:text-[#c89f45]">View receipt / manage billing →</Link>
            </div>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle size={28} className="text-red-400 mx-auto mb-6" />
            <p className="font-serif text-2xl">
              {state.status === "timeout" ? "Still confirming…" : state.status === "expired" ? "Checkout expired" : "Something went wrong"}
            </p>
            <p className="text-sm text-[#888] mt-3">
              {state.status === "timeout"
                ? "Stripe is taking a moment. Your plan will activate as soon as payment confirms — refresh in a minute."
                : "No charge was made. Try again or contact us."}
            </p>
            <div className="mt-8 flex flex-col gap-2">
              <Button onClick={() => nav("/pricing")} className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md w-full" data-testid="back-to-pricing">
                Back to pricing
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
